import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  postDevicermstateWithKeepalive,
  postSVCResultWithKeepalive,
  saveDataWithKeepalive,
} from '../apiservice/apiService';
import { getItem, setItem } from '../auth/localStorageManager';
import { resourcesVersion } from '../common/constants';
import availableLangsList from '../common/faq_guide_languages.json';
import { GUIDEBASEURL, MONITOR_GUIDEBASEURL } from '../environment';
import { formatUtcDateTime } from './timezoneUtils';
import { useTimezoneStore } from '../store/timezoneStore';
import { createSvcId } from './serviceId';
dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(timezone);

export function normalizeText(text = '') {
  return text
    .replace(/&nbsp;/g, '') // decode non-breaking spaces
    .replace(/&emsp;/g, '') // decode em spaces
    .replace(/&ensp;/g, '') // decode en spaces
    .replace(/&thinsp;/g, ' ') // decode thin spaces
    .replace(/ /gi, '') // remove any remaining em-spaces
    .replace(/\n{2,}/g, '') // normalize blank lines
    .replace(/<br\s*\/?>/gi, '\n') // convert <br> to newline first
    .replace(/(?=[\u2460-\u24FF])/g, '\n')
    .trim();
}

export function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

export function getDiagnosticResultFromHassCount(deviceType, hassDataCnt) {
  let hassDataCntString = null;

  if (deviceType == '00') {
    // 냉장고
    if (hassDataCnt >= 100) {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_EXIST';
    } else if (hassDataCnt > 0 && hassDataCnt < 100) {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_SHORTAGE';
    } else {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_NO_EXIST';
    }
  } else if (deviceType == '20' || deviceType == '60' || deviceType == '61') {
    // 세탁기, 에어컨
    if (hassDataCnt == 0) {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_NO_EXIST';
    } else if (hassDataCnt == 1) {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_SHORTAGE';
    } else if (hassDataCnt == 2) {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_EXIST';
    }
  } else if (deviceType == '70') {
    // 공기청정기 (모니터링 전용)
    hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_MONITORING_ONLY';
  } else {
    // 오븐
    if (hassDataCnt > 0) {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_EXIST';
    } else {
      hassDataCntString = 'SELECT_DIAGNOSIS_HASSDATA_NO_EXIST';
    }
  }

  return hassDataCntString;
}

// True = connect button hidden. `supported` is decided by the BE (product has no web page tab);
// undefined (older BE / cached response) is treated as supported.
export function checkStartDiagnosis(device) {
  return (
    device.supported === false ||
    (device.deviceType === '60' && device.multiHRM === '1')
  );
}

// Label shown instead of the connect button when checkStartDiagnosis is true.
export function getNotSupportedLabelKey(device) {
  return device.supported === false
    ? 'SELECT_DIAGNOSIS_TITLE_LINK_NOT_SUPPORTED_PRODUCT'
    : 'SELECT_DIAGNOSIS_TITLE_LINK_NOT_SUPPORTED';
}

export const getServiceManagerId = (urlParams, sysId) => {
  if (sysId === 'ezone') {
    return urlParams?.['agentId'] || null;
  }
  if (sysId === null || sysId === undefined) {
    return urlParams?.['managerId'] || urlParams?.['svcManagerId'] || null;
  }
  return null;
};

export const getServiceAsId = (urlParams, sysId, asId) => {
  if (sysId === 'ezone') {
    return urlParams?.svcId || asId || null;
  }
  return urlParams?.svcId || null;
};

export const createDiagnosisData = (
  data,
  dataType,
  svcAsId,
  svcManagerId,
  redirectParameters,
  svcPinCode = null,
  svcUpdateTime = null,
  visit_required = false
) => {
  return {
    svcAsId,
    svcManagerId,
    dataType: dataType,
    data: data,
    svcId: redirectParameters?.svcId ?? null,
    svcSymptom: redirectParameters?.svcSymptom ?? null,
    svcSymptomCode: redirectParameters?.svcSymptomCode ?? null,
    deviceUUID: redirectParameters?.uuid ?? null,
    countryCode: redirectParameters?.countryCode ?? null,
    createDate: redirectParameters?.createDate ?? null,
    createTime: redirectParameters?.createTime ?? null,
    region: redirectParameters?.region ?? null,
    pinCode: svcPinCode,
    updatedTime: svcUpdateTime,
    visitRequired: visit_required,
  };
};

export const getSVCID = svcId => {
  return svcId || createSvcId();
};

/** Formats a UTC timestamp using the currently selected display timezone. */
export const formatActiveTimezoneDateTime = (
  value,
  format = 'YYYY-MM-DD HH:mm:ss'
) => formatUtcDateTime(value, useTimezoneStore.getState().getActiveTimezone(), format);

export const getDisplayTextOnResult = (t, value) => {
  var str = value.toUpperCase();
  var text = t('REMOTE_DIAGNOSIS_RESULT_' + str);
  text = text ? text : value;
  return text;
};

export function find_text(current_state, t) {
  const stateConfig = {
    NEW: {
      key: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_NEW',
    },
    CONNECTED: {
      key: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_CONNECTED',
    },
    NEEDS_REFRESH: {
      key: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_NEED_REFRESH',
    },
    FAIL_SMS: {
      key: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_FAIL_SMS',
    },
    FAIL_SAVE_IMAGE: {
      baseKey: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_FAIL_SMS',
      detailKey: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_FAIL_SAVE_IMAGE',
    },
    ERROR: {
      baseKey: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_FAIL_SMS',
      detailKey: 'REMOTE_DIAGNOSIS_SHARESCREEN_STATUS_ERROR',
    },
  };

  const config = stateConfig[current_state];

  if (!config) {
    return t('REMOTE_DIAGNOSIS_MONITORING_STATUS');
  }

  if (config.detailKey) {
    return t(config.baseKey) + '\n(' + t(config.detailKey) + ')';
  }

  return t(config.key);
}
export const getGuideUrlInfo = (language, type, configinfo) => {
  const isGuideAvailable = availableLangsList.includes(language);

  if (isGuideAvailable) {
    let guideUrl;
    if (type == 'Upper') {
      guideUrl = GUIDEBASEURL + configinfo + language + '.jpg';
    } else {
      guideUrl =
        MONITOR_GUIDEBASEURL +
        configinfo +
        language +
        '_' +
        resourcesVersion +
        '.JPG';
    }

    return {
      isGuideAvailable: true,
      guideUrl,
    };
  }

  return {
    isGuideAvailable: false,
    guideUrl: null,
  };
};

export function getPinCodeTextKey(deviceType) {
  const pinCodeKeyMap = {
    70: null, // Airpurifier - no PIN code text
    71: null, // Humidifier - no PIN code text
    80: 'MONITORING_CONTROL_PIN_CODE_ROBOT_CLEANER', // Robot Vacuum
    '00': 'MONITORING_CONTROL_PIN_CODE_REF', // Refrigerator
    60: 'MONITORING_CONTROL_PIN_CODE_AIR_CONDITIONER', // Airconditioner
    61: 'MONITORING_CONTROL_PIN_CODE_AIR_CONDITIONER', // Airconditioner
    20: 'MONITORING_CONTROL_PIN_CODE_WASHER', // Washer
    30: 'MONITORING_CONTROL_PIN_CODE_DRYER', // Dryer
    38: 'MONITORING_CONTROL_PIN_CODE_DRYER', // Steam Closet
    10: 'MONITORING_CONTROL_PIN_CODE_KIMCHI_REF', // Kimchi Refrigerator
    50: 'MONITORING_CONTROL_PIN_CODE_OVEN', // Oven
    40: 'MONITORING_CONTROL_PIN_CODE_DISHWASHER', // Dishwasher
    90: 'MONITORING_CONTROL_PIN_CODE_WATERPURIFIER', // Water Purifier
  };

  return (
    pinCodeKeyMap[deviceType] ||
    'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_OTHER'
  );
}

export function getForcedControlConfig(deviceType) {
  const forcedControlConfigMap = {
    70: {
      // Airpurifier
      shouldHideForcedControl: true,
      guideTextKey: null,
      operationsTextKey: null,
    },
    71: {
      // Humidifier
      shouldHideForcedControl: true,
      guideTextKey: null,
      operationsTextKey: null,
    },
    80: {
      // Robot Vacuum
      shouldHideForcedControl: true,
      guideTextKey: null,
      operationsTextKey: null,
    },
    '00': {
      // Refrigerator
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_REF',
    },
    60: {
      // Airconditioner
      shouldHideForcedControl: false,
      guideTextKey: 'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_AIRCON',
    },
    61: {
      // Airconditioner
      shouldHideForcedControl: false,
      guideTextKey: 'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_AIRCON',
    },
    20: {
      // Washer
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_OTHER',
    },
    30: {
      // Dryer
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_OTHER',
    },
    38: {
      // Steam Closet
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_OTHER',
    },
    10: {
      // Kimchi Refrigerator
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_REF',
    },
    50: {
      // Oven
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_OVEN',
    },
    40: {
      // Dishwasher
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_OTHER',
    },
    90: {
      // Water Purifier
      shouldHideForcedControl: false,
      guideTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_GUIDE_OTHER',
      operationsTextKey:
        'REMOTE_DIAGNOSIS_MONITORING_FORCED_REMOTE_CONTROL_CHECK_OP_OTHER',
    },
  };

  return (
    forcedControlConfigMap[deviceType] || {
      shouldHideForcedControl: true,
      guideTextKey: null,
      operationsTextKey: null,
    }
  );
}
export const clearLocalStrorageData = () => {
  if (getItem('fhubfile') != 'default') {
    setItem('fhubimage', '/front/static/images/img/no_image_available.jpg');
    setItem('fhubfile', 'default');
    setItem('fhubTab', 'default');
  }
  setItem('current_state', '');
  setItem('session_token', '');
};

const svcResultObj = (redirectParameters, urlParams) => {
  let svcResultObj = {
    serviceInfo: {
      svcId: redirectParameters.svcId,
      svcManagerId: redirectParameters.svcManagerId,
      svcAsId: redirectParameters.svcId.replace('svc', ''),
      saUid: redirectParameters.saUid,
      svcSymptomCode: null,
      modelId: redirectParameters.modelId,
      modelName: redirectParameters.modelName,
      serialNumber: redirectParameters.serialNumber,
      deviceUUID: redirectParameters.uuid,
      pinCode: null,
      updatedTime: null,
      countryCode: redirectParameters.countryCode,
      sysId: urlParams?.['sys_id'],
    },
    helpRslt: '0',
    funcRslt: [
      { funcNo: 0, funcNm: '진단결과', rslt: false },
      { funcNo: 1, funcNm: '사용이력', rslt: false },
      { funcNo: 2, funcNm: '점검코드이력', rslt: false },
      { funcNo: 3, funcNm: '모니터링', rslt: false },
      { funcNo: 4, funcNm: '원격제어', rslt: false },
      { funcNo: 5, funcNm: '문자전송', rslt: false },
    ],
    opinion: 'Logging out with closing the browser page/window',
    serviceMeasure: [],
    date: new Date(),
    createDate: redirectParameters.createDate,
    createTime: redirectParameters.createTime,
    region: redirectParameters.region,
  };
  let svcId = urlParams?.['svcId'];
  let svcSymptomCode = urlParams?.['svcSymptomCode'];
  let gcicSvcManagerId = urlParams?.['svcManagerId'];
  let svcAsId = urlParams?.['as_id'] || (svcId ? svcId.replace('svc', '') : '');

  if (svcId && gcicSvcManagerId && svcAsId) {
    svcResultObj.serviceInfo.svcId = svcId;
    svcResultObj.serviceInfo.svcSymptomCode = svcSymptomCode;
    svcResultObj.serviceInfo.svcManagerId = gcicSvcManagerId;
    svcResultObj.serviceInfo.svcAsId = svcAsId;
    svcResultObj.createDate = urlParams?.['createDate'];
    svcResultObj.createTime = urlParams?.['createTime'];
    svcResultObj.opinion =
      'Logging out with closing the browser page/window with GCIC data';
  }
  return svcResultObj;
};
let isUnloaded = false;

// Export a function to reset isUnloaded flag for testing purposes
export const __setIsUnloaded = (value) => {
  isUnloaded = value;
};

export const handleBeforeUnload = (urlParams, redirectParameters) => {
  if (isUnloaded) return;
  isUnloaded = true;

  const asId = urlParams?.['as_id'] ?? null;
  const sysId = urlParams?.['sys_id'];
  const svcAsId = getServiceAsId(urlParams, sysId, asId);
  const svcManagerId = getServiceManagerId(urlParams, sysId);
  let saveDataPayload = createDiagnosisData(
    'endTime',
    'endTime',
    svcAsId,
    svcManagerId,
    redirectParameters
  );
  saveDataPayload = { ...saveDataPayload, sysId: sysId };

  saveDataWithKeepalive(saveDataPayload);

  const svcresultPayload = svcResultObj(redirectParameters, urlParams);
  postSVCResultWithKeepalive(svcresultPayload);

  const rmModeEnabled = sessionStorage.getItem('rmModeEnabled');
  if (rmModeEnabled === 'true') {
    const rmStatePayload = {
      rmMode: 'false',
    };
    const uuid = redirectParameters.uuid;
    const saUid = redirectParameters.saUid;
    postDevicermstateWithKeepalive({ uuid, saUid, payload: rmStatePayload });
    sessionStorage.removeItem('rmModeEnabled');
  }
  clearLocalStrorageData();
};

export const capitalizeFirstLetter = str => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Converts all UTC timestamps in a string to the active timezone.
 * Assumes input timestamps are in UTC with format: YYYY-MM-DD HH:mm:ss
 * @param {string} text - Input text potentially containing UTC timestamps
 * @returns {string} - Text with all UTC timestamps converted to active timezone
 */
export const convertUtcTimeInText = (text) => {
  if (!text || typeof text !== 'string') {
    return text ?? '';
  }
  
  // Regex to match UTC timestamps in format: YYYY-MM-DD HH:mm:ss
  const utcTimestampRegex = /\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/g;
  
  return text.replace(utcTimestampRegex, (match) => {
    try {
      // Convert matched timestamp to active timezone
      return formatActiveTimezoneDateTime(match);
    } catch (error) {
      // If conversion fails, return original match
      console.warn('Failed to convert timestamp:', match, error);
      return match;
    }
  });
};
